import type { RxJsonSchema } from 'rxdb'

/**
 * Project Document Type (Denormalized for RxDB)
 */
export interface ProjectDocument {
  id: string
  envId: string
  projectKey: string
  name: string
  icon: string | null
  iconType: string | null
  iconColor: string | null
  parentProjectId: string | null
  startDate: number | null
  endDate: number | null
  ownerId: string | null
  description: string | null
  visibility: 'private' | 'viewOnly' | 'open'
  sortOrder: string | null
  estimationType?: 'none' | 'freeform' | 'fibonacci' | 'tshirt' | 'exponential' | null
  isAccountableEnabled?: boolean
  isWorkItemTypeEnabled?: boolean
  statusMode?: 'simple' | 'advanced' | null
  statusMigrationInProgress?: boolean
  /**
   * Whether this project has the Super Agent enabled. Server-authoritative — the
   * backend derives it from the project's Super Agent config and never reads it back
   * from a push. The Super Agent member is workspace-level, so this is what scopes it
   * to the projects that can actually dispatch to it.
   */
  superAgentEnabled?: boolean
  isCyclesEnabled?: boolean
  cycleDurationWeeks?: number | null
  coverImageUrl: string | null
  coverImageType: string | null
  coverImageMeta: string | null
  isDeleted: boolean

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * RxDB Schema for Project
 */
export const projectSchema: RxJsonSchema<ProjectDocument> = {
  version: 2,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    projectKey: {
      type: 'string',
      maxLength: 10,
    },
    name: {
      type: 'string',
      maxLength: 50,
    },
    icon: {
      type: ['string', 'null'],
      maxLength: 50,
    },
    iconType: {
      type: ['string', 'null'],
      maxLength: 10,
    },
    iconColor: {
      type: ['string', 'null'],
      maxLength: 7,
    },
    parentProjectId: {
      type: ['string', 'null'],
      maxLength: 36,
    },
    startDate: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    endDate: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    ownerId: {
      type: ['string', 'null'],
      maxLength: 36,
    },
    description: {
      type: ['string', 'null'],
      maxLength: 5000,
    },
    visibility: {
      type: 'string',
      enum: ['private', 'viewOnly', 'open'],
      default: 'private',
    },
    sortOrder: {
      type: ['string', 'null'],
      maxLength: 100,
    },
    estimationType: {
      type: ['string', 'null'],
      enum: ['none', 'freeform', 'fibonacci', 'tshirt', 'exponential', null],
    },
    isAccountableEnabled: {
      type: 'boolean',
      default: false,
    },
    isWorkItemTypeEnabled: {
      type: 'boolean',
      default: false,
    },
    statusMode: {
      type: ['string', 'null'],
      enum: ['simple', 'advanced', null],
    },
    statusMigrationInProgress: {
      type: 'boolean',
      default: false,
    },
    superAgentEnabled: {
      type: 'boolean',
      default: false,
    },
    isCyclesEnabled: {
      type: 'boolean',
      default: false,
    },
    cycleDurationWeeks: {
      type: ['number', 'null'],
      minimum: 1,
      maximum: 52,
      multipleOf: 1,
    },
    coverImageUrl: {
      type: ['string', 'null'],
      maxLength: 2048,
    },
    coverImageType: {
      type: ['string', 'null'],
      maxLength: 20,
    },
    coverImageMeta: {
      type: ['string', 'null'],
      maxLength: 2048,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'projectKey',
    'name',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    'envId',
    'projectKey',
    'updatedOn',
    ['envId', 'isDeleted'],
  ],
}

/**
 * Migration strategies for project schema.
 *
 * v0 -> v1: backfill `sortOrder` (added field, nullable). Removes the
 * wipe-baseline dependency — old docs with the missing property remain valid.
 *
 * v1 -> v2: added `superAgentEnabled` (server-authoritative). Defaults to false so a
 * migrated project hides the Super Agent until the server confirms otherwise on the
 * next pull — the safe direction: a project that IS enabled gets it back one pull
 * later, whereas defaulting true would keep showing the agent on disabled projects.
 */
export const projectMigrationStrategies = {
  1: (oldDoc: any): any => ({
    ...oldDoc,
    sortOrder: oldDoc.sortOrder ?? null,
  }),
  2: (oldDoc: any): any => ({
    ...oldDoc,
    superAgentEnabled: oldDoc.superAgentEnabled ?? false,
  }),
}
